import {h} from 'preact';
import {lazy, Suspense, useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import './Page.scss';
import '@assets/styles/components/_faqPagePreview.scss';
import '@assets/styles/components/_scrollbar.scss';
import prependFaqPagePreview from '@assets/helpers/faqPage/prependFaqPagePreview';
import MobileLayout from './MobileLayout';
import {prepareTranslatedFaqs, prepareTranslatedQuestion} from '../../helpers/faqHelpers';
import {useWindowSize} from '@scripttag/hooks/useWindowSize';
import {faqSearchByWordHandler} from '@assets/helpers/searchHelper';
import {FaqPageProvider} from '@assets/contexts/faqPageContext';
import {i18nPage} from '@functions/helpers/translation/i18n';
import useGoogleFonts from '@scripttag/widgets/hooks/useGoogleFonts';
import getContentFromHTMLString from '@assets/helpers/getContentFromHTMLString';

const FaqPageDesktopView = lazy(() =>
  import('@assets/components/FaqPage/Preview/FaqPageDesktopView')
);
const FaqPageMobileView = lazy(() =>
  import('@assets/components/FaqPage/Preview/FaqPageMobileView')
);

/** @jsx h Tell babel to use h for JSX */
export default function Page({getData, putData, postData}) {
  const {
    faqPageSetting: settings,
    widgetSetting: widgetSettings,
    translation = {}
  } = window.AVADA_FAQ;
  const {shopId} = window.AVADA_APP_FAQS_STATUS;
  const categories = prepareTranslatedFaqs();
  const questions = prepareTranslatedQuestion();

  const [selectedCategoryId, setCategoryIdSelected] = useState('all');
  const [questionSelected, setQuestionSelected] = useState(null);
  const handleSelectCategory = useCallback(id => setCategoryIdSelected(id), []);
  const handleSelectQuestion = useCallback(question => {
    window.scrollTo({
      top: 0
    });
    setQuestionSelected(question);
  }, []);

  const [keySearch, setKeySearch] = useState('');
  const handleSearch = useCallback(value => setKeySearch(value), []);
  const searchHandler = faqSearchByWordHandler();

  const filterCategories = useMemo(() => {
    if (selectedCategoryId === 'all') return categories;
    return categories.filter(x => x.categoryId === selectedCategoryId);
  }, [selectedCategoryId]);

  const searchQuestions = useMemo(() => {
    const documents = (() => {
      if (selectedCategoryId === 'all') return questions;
      return questions.filter(x => x.categoryId === selectedCategoryId);
    })();
    if (!keySearch) return documents;
    return searchHandler(
      keySearch,
      documents.map(x => ({...x, textAnswer: getContentFromHTMLString(x.answer) || ''}))
    );
  }, [selectedCategoryId, keySearch]);

  const defaultCategory = {
    categoryId: 'all',
    question: i18nPage({phrase: '"All" tabs in FAQ page', translation})
  };

  const props = {
    shopId,
    defaultCategory,
    keySearch,
    handleSearch,
    selectedCategoryId,
    handleSelectCategory,
    filterCategories,
    searchQuestions,
    categories,
    questions,
    settings,
    getData,
    putData,
    questionSelected,
    handleSelectQuestion,
    postData,
    isPreview: false,
    i18n: (phrase, variables, prefix) => i18nPage({phrase, translation, variables, prefix})
  };

  prependFaqPagePreview(settings, widgetSettings);
  useGoogleFonts([widgetSettings.headingTextFont]);

  const {isMobile} = useWindowSize();

  /** @type {JSX.Element} */
  const content = useMemo(() => {
    if (isMobile) {
      return (
        <MobileLayout>
          <FaqPageMobileView />
        </MobileLayout>
      );
    }
    return <FaqPageDesktopView />;
  }, [isMobile]);

  return (
    <FaqPageProvider value={props}>
      <Suspense fallback={<div />}>{content}</Suspense>
    </FaqPageProvider>
  );
}

Page.propTypes = {
  getData: PropTypes.func,
  putData: PropTypes.func,
  postData: PropTypes.func
};
